// The phone shell: a deep-teal header (party · role · zh/EN), the page, and a
// bottom tab bar. Today · My jobs · Work order · Owed; Owed is for external
// parties, internal crews see it dimmed with "external only" (signed mockup).
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { CalendarDays, CircleDollarSign, FileText, Globe, List,ReceiptText,Users } from "lucide-react";
import { cn } from "@aiw/ui";
import { useT } from "../i18n";
import { useRole } from "../hooks/useMe";
import { useMyJobs, pickCurrent } from "../hooks/useReads";
import { env } from "../lib/env";

interface Head { title: string; sub?: string | null }
const HeadCtx = createContext<{ set: (h: Head) => void }>({ set: () => {} });

/** Pages set the header title and sub-line. */
export function usePageHead(title: string, sub?: string | null) {
  const { set } = useContext(HeadCtx);
  useEffect(() => { set({ title, sub: sub ?? null }); }, [set, title, sub]);
}

export function Frame() {
  const { t, lang, toggle } = useT();
  const { isExternal, isInternal,isManager, partyName, isLoading } = useRole();
  const jobs = useMyJobs();
  const current = pickCurrent(jobs.data);
  const [head, setHead] = useState<Head>({ title: t("title_today") });
  const ctx = useMemo(() => ({ set: setHead }), []);
  const loc = useLocation();
  const initials = partyName.split(/\s+/).map((w) => w[0] ?? "").join("").slice(0, 2).toUpperCase() || "··";

  const tabs: { to: string; label: string; icon: ReactNode; disabled?: boolean; active?: (p: string) => boolean }[] = [
    { to: "/", label: t("tab_today"), icon: <CalendarDays />, active: (p) => p === "/" },
    { to: "/jobs", label: t("tab_jobs"), icon: <List />, active: (p) => p.startsWith("/jobs") },
    { to: current ? `/work-order/${current.ref}` : "/work-order", label: t("tab_wo"), icon: <FileText />, active: (p) => p.startsWith("/work-order") },
    ...(isManager?[{to:'/team',label:t('tab_team'),icon:<Users/>,active:(p:string)=>p.startsWith('/team')}]:[]),
    ...(isManager?[{to:'/pricing',label:t('tab_pricing'),icon:<ReceiptText/>,active:(p:string)=>p.startsWith('/pricing')}]:[]),
    { to: "/owed", label: isInternal ? t("tab_owed_external_only") : t("tab_owed"), icon: <CircleDollarSign />, disabled: isInternal||!isManager, active: (p) => p.startsWith("/owed") },
  ];

  return (
    <HeadCtx.Provider value={ctx}>
      <div className="flex min-h-screen flex-col bg-background">
        <header className="bg-rail text-rail-foreground">
          <div className="page pb-3 pt-[max(12px,env(safe-area-inset-top))]">
            <div className="flex items-center gap-2">
              {/* The Artemis Flooring symbol (packages/ui/brand); the small cut keeps two floor seams at this size. */}
              <img src="/brand/artemis-symbol-small-on-navy.svg" alt="Artemis Flooring" width={32} height={32} className="h-8 w-8 shrink-0" />
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-rail-foreground font-display text-sm font-semibold text-rail" aria-hidden>{initials}</span>
              <span className="min-w-0 truncate text-sm">
                {isLoading ? "…" : <>{partyName} · {isExternal ? t("role_external") : isInternal ? t("role_internal") : ""}</>}
              </span>
              {env.mock && <span className="rounded bg-rail-active px-1.5 font-mono text-micro text-rail-muted" title="No Supabase config: the portal runs on the in-browser mock of services/api">{t("mock_chip")}</span>}
              <button
                type="button"
                onClick={toggle}
                lang={lang === "zh" ? "en" : "zh-CN"}
                className="ml-auto inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-md bg-rail-active px-3 text-sm font-medium text-rail-foreground"
                aria-label={lang === "zh" ? "Switch to English" : "切换到中文"}
              >
                <Globe className="h-4 w-4" aria-hidden />{t("lang_toggle")}
              </button>
            </div>
            <h1 className="mt-3 font-display text-2xl font-semibold leading-tight">{head.title}</h1>
            {head.sub && <p className="mt-1 text-sm text-rail-muted">{head.sub}</p>}
          </div>
        </header>

        <main className="flex-1 pb-[calc(64px+env(safe-area-inset-bottom))]">
          <div className="page py-4">
            <Outlet />
          </div>
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-10 border-t bg-card pb-[env(safe-area-inset-bottom)]" aria-label="Sections">
          <ul className="page grid px-0" style={{gridTemplateColumns:`repeat(${tabs.length},minmax(0,1fr))`}}>
            {tabs.map((tab) => {
              const on = tab.active ? tab.active(loc.pathname) : loc.pathname === tab.to;
              return (
                <li key={tab.to}>
                  <NavLink
                    to={tab.to}
                    aria-disabled={tab.disabled || undefined}
                    onClick={(e) => { if (tab.disabled) e.preventDefault(); }}
                    className={cn(
                      "flex min-h-[56px] flex-col items-center justify-center gap-0.5 px-1 text-center text-micro font-medium leading-4 [&_svg]:h-5 [&_svg]:w-5",
                      on ? "text-primary" : "text-muted-foreground",
                      tab.disabled && "opacity-45",
                    )}
                    aria-current={on ? "page" : undefined}
                  >
                    {tab.icon}
                    <span className="max-w-full truncate">{tab.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </HeadCtx.Provider>
  );
}
